import { CampaignType, JourneyStepCategory } from '@dev/ee-api/ee-api-models';
import { ContentLoader, DividedBox, Panel, TertiaryButton, Typography } from '@dev/ee-components';
import { convertToLocalTime } from '@dev/ee-tools';
import { TFunction } from 'i18next';
import { isEmpty, join } from 'lodash-es';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { push as Push } from 'redux-first-history';
import { CampaignsInterface, PickedCampaignInterface } from '@/store/campaigns/types';
import useMobile from '@/services/hooks/useMobile';
import { formatNumber } from '@/services/tools/formatNumber';
import { CampaignsPaths } from '@/services/dictionaries/Paths';
import { StyledProps } from '@/components/Campaigns/CampaignsInterface';
import { getRecipientsCount, getCampaignStartDate } from '../../helperFunctions';
import { ContentLoaderWrapper } from '../..';

const TopContentWrapper = styled.div<StyledProps>`
    margin-bottom: ${({ isAutomated, isMobile }): string =>
        isAutomated ? (isMobile ? '24px' : '32px') : isMobile ? '46px' : '64px'};
`;
const BasicInfoWrapper = styled.div<StyledProps>`
    display: flex;
    flex-direction: ${({ isMobile }): string => (isMobile ? 'column' : 'row')};
    width: 100%;
    justify-content: space-between;
    min-height: 160px;
    .GridCart {
        width: ${({ isMobile }): string => !isMobile && '20%'};
        margin-bottom: ${({ isMobile }): string => isMobile && '24px'};
        .view-contacts-link {
            margin-top: ${({ isMobile }): string => !isMobile && '6px'};
            margin-bottom: 32px;
        }
    }
`;
const DividedBoxWrapper = styled.div<StyledProps>`
    width: ${({ isMobile }): string => !isMobile && '75%'};
    div {
        min-height: 100%;
    }
`;

export const OverviewStats = (props: { campaigns: CampaignsInterface; name: string; type: string }): JSX.Element => {
    const { campaigns, name, type } = props;
    const { pickedCampaign, loading, abSplitHeaderControls } = campaigns;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();

    return (
        <TopContentWrapper isAutomated={pickedCampaign?.campaign?.Type === CampaignType.Automated} isMobile={isMobile}>
            <BasicInfoWrapper isMobile={isMobile}>
                <Panel
                    description={
                        <>
                            {!loading && !isEmpty(pickedCampaign.campaign) ? (
                                <Typography variant={'h1'}>
                                    {formatNumber(
                                        parseInt(
                                            getRecipientsCount(
                                                pickedCampaign?.campaign,
                                                pickedCampaign?.simpleStats?.CampaignTemplatesSummary,
                                                abSplitHeaderControls.currentTemplate,
                                                abSplitHeaderControls.displayMode
                                            ),
                                            10
                                        )
                                    )}
                                </Typography>
                            ) : (
                                <ContentLoaderWrapper>
                                    <ContentLoader mode="row" height={16} />
                                </ContentLoaderWrapper>
                            )}
                            <Typography className="view-contacts-link" variant="h3">
                                {t('recipents')}
                            </Typography>
                            <TertiaryButton
                                text={t('viewcontacts')}
                                onClick={(): void => {
                                    dispatch(
                                        Push(
                                            CampaignsPaths.CampaignActivity.replace(':type', type).replace(
                                                ':name',
                                                name
                                            )
                                        )
                                    );
                                }}
                            />
                        </>
                    }
                    loading={!getRecipientsCount(pickedCampaign?.campaign)}
                    headerPadding={isMobile ? '32px' : '24px 32px'}
                />
                <DividedBoxWrapper isMobile={isMobile}>
                    <DividedBox flexTextAlign="flex-start" rows={prepareBasicInfoRows(pickedCampaign, loading, t)} />
                </DividedBoxWrapper>
            </BasicInfoWrapper>
        </TopContentWrapper>
    );
};

const checkAudience = (pickedCampaign: PickedCampaignInterface): string => {
    const segmentNames = pickedCampaign?.campaign?.Targets?.SegmentNames;
    const listNames = pickedCampaign?.campaign?.Targets?.ListNames;

    const listAndSegments = [...(segmentNames || []), ...(listNames ?? [])];
    return join(listAndSegments, ', ');
};

const checkTrigger = (pickedCampaign: PickedCampaignInterface, t: TFunction): string => {
    const triggerAction = (): string => {
        switch (pickedCampaign?.campaign?.AutomationTrigger?.Action) {
            case JourneyStepCategory.SendEmail:
                return t('automationssendemail');
            case JourneyStepCategory.SendSMS:
                return t('automationssendsms');
            case JourneyStepCategory.OpensEmail:
                return t('automationsopensemail');
            case JourneyStepCategory.ClicksLink:
                return t('automationsclickslink');
            case JourneyStepCategory.RemoveFromList:
                return t('automationsremovefromlist');
            case JourneyStepCategory.AddToList:
                return t('automationsaddtolist');
            case JourneyStepCategory.JoinsList:
                return t('automationsjoinslist');
            case JourneyStepCategory.StatusChanged:
                return t('automationsstatuschanged');
            case JourneyStepCategory.SendWebhook:
                return t('automationssendwebhook');
            case JourneyStepCategory.Manually:
                return t('automationsmanually');
            case JourneyStepCategory.IsOnList:
                return t('automationsisonlist');
            case JourneyStepCategory.Delay:
                return t('automationsdelay');
            case JourneyStepCategory.TriggerJourney:
                return t('automationstriggerjourney');
            default:
                return '';
        }
    };

    return `${t('contact')} ${triggerAction()} ${
        pickedCampaign?.campaign?.AutomationTrigger?.Subject
            ? `"${pickedCampaign?.campaign?.AutomationTrigger?.Subject}"`
            : ''
    } ${
        pickedCampaign?.campaign?.AutomationTrigger?.Target
            ? `"${pickedCampaign?.campaign?.AutomationTrigger?.Target}"`
            : ''
    }
    `;
};

const prepareBasicInfoRows = (
    pickedCampaign: PickedCampaignInterface,
    loading: boolean,
    t: TFunction
): { label: JSX.Element; value: JSX.Element }[][] => {
    const campaignStartTitle = pickedCampaign?.campaign?.Type === CampaignType.Automated ? t('created') : t('sent');
    const isFromAutomation = pickedCampaign?.campaign?.FromAutomation;

    if (!loading && isEmpty(pickedCampaign.campaign)) {
        return [
            [
                {
                    label: (
                        <div style={{ marginRight: '32px', width: '85px' }}>
                            <ContentLoaderWrapper>
                                <ContentLoader mode="row" height={16} />
                            </ContentLoaderWrapper>
                        </div>
                    ),
                    value: (
                        <ContentLoaderWrapper>
                            <ContentLoader mode="row" height={16} />
                        </ContentLoaderWrapper>
                    ),
                },
                {
                    label: (
                        <div style={{ marginRight: '32px', width: '85px' }}>
                            <ContentLoaderWrapper>
                                <ContentLoader mode="row" height={16} />
                            </ContentLoaderWrapper>
                        </div>
                    ),
                    value: (
                        <ContentLoaderWrapper>
                            <ContentLoader mode="row" height={16} />
                        </ContentLoaderWrapper>
                    ),
                },
            ],
        ];
    }
    return [
        [
            {
                label: (
                    <div style={{ marginRight: '32px', width: '85px' }}>
                        <Typography variant="body2">
                            {pickedCampaign?.campaign?.AutomationTrigger ? t('trigger') : t('triggeraudience')}
                        </Typography>
                    </div>
                ),
                value: (
                    <Typography variant="h4">
                        {pickedCampaign?.campaign?.AutomationTrigger
                            ? checkTrigger(pickedCampaign, t)
                            : checkAudience(pickedCampaign)}
                    </Typography>
                ),
            },
            {
                label: (
                    <div style={{ marginRight: '32px', width: '85px' }}>
                        <Typography variant="body2">{!isFromAutomation ? campaignStartTitle : t('created')}</Typography>
                    </div>
                ),
                value: (
                    <Typography variant="h4">
                        {!isFromAutomation
                            ? getCampaignStartDate(pickedCampaign.campaign)
                            : convertToLocalTime(pickedCampaign?.campaign?.DateAdded) || 'N/A'}
                    </Typography>
                ),
            },
        ],
    ];
};
